import { ReactNode } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Button } from '../../ui/button';
import { IconButton } from '../studioChrome';
import { FixtureMark } from './Explorer';
import { FixtureResult } from '@/reports/bundles/types';

/** One thing the preview can read, as its menu lists it. */
export interface PreviewChoice {
  key: string;
  label: string;
  /** a fixture's file name, set in monospace */
  file?: boolean;
  /** how the fixture came out at the last save */
  result?: FixtureResult;
  /** what it says beside: "not saved" */
  note?: string;
  checked: boolean;
  onSelect: () => void;
}

/** A run of choices in the preview's menu, under a heading when it has one. */
export interface PreviewGroup {
  key: string;
  label?: string;
  choices: PreviewChoice[];
  /** said under the choices */
  note?: string;
}

/**
 * The preview: a side bar on the right of every screen, shown or hidden from
 * the title bar, with what it reads picked from its menu — a fixture, what is
 * typed in Try it, nothing entered; for a run report, the sample run or a real
 * one; for a schema on its own, its form or a report that reads it.
 */
export function PreviewSidebar({ label, groups, onOpenMenu, onHide, banner, footer, children }: {
  /** what the preview reads now, as its menu button says */
  label: string;
  groups: PreviewGroup[];
  /** the menu opened: the time to read what its choices need */
  onOpenMenu?: () => void;
  onHide: () => void;
  /** above the report: what the preview is being used for, as while picking */
  banner?: ReactNode;
  /** under the report: a check being written from what was picked */
  footer?: ReactNode;
  children: ReactNode;
}) {
  const shown = groups.filter((g) => g.choices.length > 0);
  return (
    <section aria-label={I18n.get('reportBundlePreview')} className="flex h-full min-w-0 flex-col bg-muted/40">
      <div className="flex h-9 shrink-0 items-center gap-2 border-b bg-background pr-1.5 pl-3">
        <h2 className="text-[0.7rem] font-semibold tracking-wide text-muted-foreground uppercase">{I18n.get('reportBundlePreview')}</h2>
        <DropdownMenu onOpenChange={(open) => { if (open) onOpenMenu?.(); }}>
          <DropdownMenuTrigger render={<Button variant="outline" size="xs" className="min-w-0 gap-1.5" aria-label={I18n.get('designerPreviewWith', { source: label })} />}>
            <span className="truncate">{label}</span>
            <ChevronDown className="size-3 shrink-0" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80">
            {shown.map((group, i) => (
              <DropdownMenuGroup key={group.key}>
                {i > 0 && <DropdownMenuSeparator />}
                {group.label && <DropdownMenuLabel>{group.label}</DropdownMenuLabel>}
                {group.choices.map((choice) => <ChoiceItem key={choice.key} choice={choice} />)}
                {group.note && <p className="px-2 pb-1.5 text-xs text-muted-foreground">{group.note}</p>}
              </DropdownMenuGroup>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <IconButton label={I18n.get('designerHidePreview')} size="icon-xs" className="ml-auto" onClick={onHide}><X /></IconButton>
      </div>
      {banner}
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      {footer && <div className="max-h-[60%] shrink-0 overflow-y-auto border-t bg-background p-2">{footer}</div>}
    </section>
  );
}

function ChoiceItem({ choice }: { choice: PreviewChoice }) {
  return (
    <DropdownMenuItem onClick={choice.onSelect} aria-checked={choice.checked} role="menuitemradio" className="gap-2">
      <Check className={cn('size-3.5 shrink-0', choice.checked ? 'opacity-100' : 'opacity-0')} />
      <span className={cn('min-w-0 flex-1 truncate', choice.file && 'font-mono text-xs')}>{choice.label}</span>
      {choice.result?.verdictLabel && <span className="shrink-0 text-[0.65rem] text-muted-foreground">{choice.result.verdictLabel}</span>}
      <FixtureMark result={choice.result} />
      {choice.note && <span className="shrink-0 text-xs text-muted-foreground">{choice.note}</span>}
    </DropdownMenuItem>
  );
}
